// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { useEffect } from 'react';
import {
  Briefcase,
  GraduationCap,
  Loader2,
  Star,
  Trophy,
  Wallet,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useDashboard } from '@/app/features/dashboard';
import { useCreditStore } from '@/app/stores/credit-store';
import { PageHeader } from '@/app/components/page-layout';

export function DashboardPage() {
  const dashboard = useDashboard();
  const setBalance = useCreditStore((s) => s.setBalance);

  // Sync the credit store with server data on load.
  useEffect(() => {
    if (dashboard.data?.credit_balance) {
      setBalance(dashboard.data.credit_balance);
    }
  }, [dashboard.data?.credit_balance, setBalance]);

  if (dashboard.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (dashboard.error) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <div
          className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          Failed to load dashboard data. Please try again later.
        </div>
      </div>
    );
  }

  const data = dashboard.data;
  const creditBalance = data?.credit_balance ?? '0.0000';
  const formattedBalance = `$${parseFloat(creditBalance).toFixed(2)}`;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Welcome to FlairCV. Your career database at a glance."
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DashboardCard
          title="Experiences"
          value={String(data?.experience_count ?? 0)}
          icon={<Briefcase className="h-4 w-4" />}
          href="/experience"
          description="Work history entries"
        />
        <DashboardCard
          title="Accomplishments"
          value={String(data?.accomplishment_count ?? 0)}
          icon={<Trophy className="h-4 w-4" />}
          href="/accomplishments"
          description="Achievements tracked"
        />
        <DashboardCard
          title="Skills"
          value={String(data?.skill_count ?? 0)}
          icon={<Star className="h-4 w-4" />}
          href="/skills"
          description="Technical and soft skills"
        />
        <DashboardCard
          title="Education"
          value={String(data?.education_count ?? 0)}
          icon={<GraduationCap className="h-4 w-4" />}
          href="/education"
          description="Degrees and certifications"
        />
        <DashboardCard
          title="Credit Balance"
          value={formattedBalance}
          icon={<Wallet className="h-4 w-4" />}
          description="Available for AI generation"
          mono
        />
      </div>

      {/* Empty state guidance for new users */}
      {data &&
        data.experience_count === 0 &&
        data.accomplishment_count === 0 &&
        data.skill_count === 0 &&
        data.education_count === 0 && (
          <div className="mt-8 rounded-lg border border-dashed border-border p-8 text-center">
            <p className="text-[15px] font-medium text-foreground">
              Get started by building your career database
            </p>
            <p className="mt-1 text-[14px] text-muted-foreground">
              Add your work experiences, accomplishments, skills, and education
              to generate tailored CVs.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/experience"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <Briefcase className="h-3.5 w-3.5" />
                Add Experience
              </Link>
              <Link
                to="/accomplishments"
                className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-2 text-[14px] font-medium text-foreground transition-colors hover:bg-muted"
              >
                <Trophy className="h-3.5 w-3.5" />
                Add Accomplishment
              </Link>
              <Link
                to="/skills"
                className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-2 text-[14px] font-medium text-foreground transition-colors hover:bg-muted"
              >
                <Star className="h-3.5 w-3.5" />
                Add Skill
              </Link>
            </div>
          </div>
        )}
    </div>
  );
}

function DashboardCard({
  title,
  value,
  icon,
  href,
  description,
  mono,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  href?: string;
  description: string;
  mono?: boolean;
}) {
  const content = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </p>
        <span className="rounded-md bg-muted p-1.5 text-muted-foreground">
          {icon}
        </span>
      </div>
      <p
        className={`mt-2 text-[28px] font-semibold tracking-tight text-card-foreground ${
          mono ? 'font-mono tabular-nums' : 'tabular-nums'
        }`}
      >
        {value}
      </p>
      <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="block rounded-lg border border-border bg-card p-4 transition-colors hover:border-border/80 hover:bg-card/80"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">{content}</div>
  );
}
